const DRAFT_PREFIX = 'listing-draft:';
const AMENITIES_TTL_MS = 24 * 60 * 60 * 1000;

const EMPTY_AMENITIES = {
  transport: [],
  supermarkets: [],
  schools: [],
  leisure: [],
  parks: [],
};

function safeGetItem(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore storage errors
  }
}

function safeParseJson(raw, fallback = null) {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function draftKey(propertyId) {
  return `${DRAFT_PREFIX}${propertyId}`;
}

function normaliseDraft(raw = {}) {
  return {
    description: typeof raw.description === 'string' ? raw.description : '',
    features: Array.isArray(raw.features) ? raw.features : [],
    highlights: typeof raw.highlights === 'string' ? raw.highlights : '',
    epcRating: typeof raw.epcRating === 'string' ? raw.epcRating : '',
    customAmenities: Array.isArray(raw.customAmenities) ? raw.customAmenities : [],
    slideOrder: Array.isArray(raw.slideOrder) ? raw.slideOrder : null,
    streetViewDataUrl: typeof raw.streetViewDataUrl === 'string' ? raw.streetViewDataUrl : null,
    streetViewError: !!raw.streetViewError,
    streetViewHidden: !!raw.streetViewHidden,
    amenityCoords: raw.amenityCoords?.lat != null && raw.amenityCoords?.lng != null
      ? { lat: raw.amenityCoords.lat, lng: raw.amenityCoords.lng }
      : null,
    amenitiesCache: raw.amenitiesCache && typeof raw.amenitiesCache === 'object'
      ? {
          _ts: Number(raw.amenitiesCache._ts) || 0,
          transport: Array.isArray(raw.amenitiesCache.transport) ? raw.amenitiesCache.transport : [],
          supermarkets: Array.isArray(raw.amenitiesCache.supermarkets) ? raw.amenitiesCache.supermarkets : [],
          schools: Array.isArray(raw.amenitiesCache.schools) ? raw.amenitiesCache.schools : [],
          leisure: Array.isArray(raw.amenitiesCache.leisure) ? raw.amenitiesCache.leisure : [],
          parks: Array.isArray(raw.amenitiesCache.parks) ? raw.amenitiesCache.parks : [],
        }
      : null,
    updatedAt: Number(raw.updatedAt) || 0,
  };
}

function readRawDraft(propertyId) {
  if (!propertyId) return null;
  return safeParseJson(safeGetItem(draftKey(propertyId)), null);
}

function persistDraft(propertyId, draft) {
  if (!propertyId) return;
  safeSetItem(draftKey(propertyId), JSON.stringify(draft));
}

export function readListingDraft(propertyId) {
  return normaliseDraft(readRawDraft(propertyId) || {});
}

export function hasListingDraftValue(propertyId, field) {
  const rawDraft = readRawDraft(propertyId);
  return !!(rawDraft && Object.prototype.hasOwnProperty.call(rawDraft, field));
}

export function updateListingDraft(propertyId, updates) {
  const current = readListingDraft(propertyId);
  const next = normaliseDraft({
    ...current,
    ...updates,
    updatedAt: Date.now(),
  });
  persistDraft(propertyId, next);
  return next;
}

export function readListingAmenityCoords(propertyId) {
  const draft = readListingDraft(propertyId);
  if (draft.amenityCoords?.lat != null && draft.amenityCoords?.lng != null) {
    return draft.amenityCoords;
  }

  return null;
}

export function cacheListingAmenityCoords(propertyId, _postcode, coords) {
  if (!coords || coords.lat == null || coords.lng == null) return null;
  updateListingDraft(propertyId, { amenityCoords: { lat: coords.lat, lng: coords.lng } });
  return coords;
}

export function readListingAmenities(propertyId) {
  const draft = readListingDraft(propertyId);
  const custom = draft.customAmenities || [];

  if (draft.amenitiesCache && Date.now() - (draft.amenitiesCache._ts || 0) < AMENITIES_TTL_MS) {
    return {
      ...EMPTY_AMENITIES,
      ...draft.amenitiesCache,
      custom,
      coords: draft.amenityCoords,
    };
  }

  return {
    ...EMPTY_AMENITIES,
    custom,
    coords: draft.amenityCoords,
  };
}

export function cacheListingAmenities(propertyId, { amenities, coords }) {
  const nextAmenities = {
    _ts: Date.now(),
    ...EMPTY_AMENITIES,
    ...(amenities || {}),
  };

  updateListingDraft(propertyId, {
    amenitiesCache: nextAmenities,
    amenityCoords: coords?.lat != null && coords?.lng != null ? { lat: coords.lat, lng: coords.lng } : undefined,
  });

  return nextAmenities;
}

export function readListingStreetViewState(propertyId) {
  const draft = readListingDraft(propertyId);
  return {
    dataUrl: draft.streetViewDataUrl,
    error: !!draft.streetViewError,
    hidden: !!draft.streetViewHidden,
  };
}

export function cacheListingStreetView(propertyId, dataUrl) {
  updateListingDraft(propertyId, {
    streetViewDataUrl: dataUrl,
    streetViewError: false,
    streetViewHidden: false,
  });
}

export function updateListingStreetViewState(propertyId, updates) {
  return updateListingDraft(propertyId, updates);
}
